import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, Users, ShoppingCart, RefreshCw, DollarSign } from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
} from 'recharts';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { StatusBadge } from '../components/shared/StatusBadge';
import { channelAPI } from '../lib/api';
import { formatCurrency, formatPercent, CHART_COLORS, CHANNEL_NAMES } from '../lib/utils';

export default function ChannelsPage() {
  const [channels, setChannels] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await channelAPI.getPerformance('ytd');
        setChannels(response.data);
      } catch (err) {
        console.error('Failed to fetch channels:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-slate-200 rounded w-48 animate-pulse" />
        <div className="grid grid-cols-5 gap-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-32 bg-slate-100 rounded-xl animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  // Prepare chart data
  const revenueData = channels?.channels?.map((c) => ({
    name: CHANNEL_NAMES[c.channel] || c.channel,
    revenue: c.revenue / 100000,
    spend: c.spend / 100000,
    margin: c.contribution_margin / 100000,
  })) || [];

  const roasData = channels?.channels?.map((c) => ({
    subject: CHANNEL_NAMES[c.channel] || c.channel,
    ROAS: c.roas,
    fullMark: 6,
  })) || [];

  const efficiencyData = channels?.channels?.map((c) => ({
    name: CHANNEL_NAMES[c.channel] || c.channel,
    roas: c.roas,
    conversionRate: c.conversion_rate,
    repeatRate: c.repeat_rate,
    channel: c.channel,
  })) || [];

  return (
    <div className="space-y-6" data-testid="channels-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-slate-900">Channel Performance</h2>
          <p className="text-slate-500">Detailed analytics for each acquisition channel</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500">Top Performer:</span>
          <span className="font-medium text-primary-700">
            {CHANNEL_NAMES[channels?.top_performing] || channels?.top_performing}
          </span>
        </div>
      </div>

      {/* Channel Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {channels?.channels?.map((channel, index) => {
          const isTopPerformer = channel.channel === channels?.top_performing;
          const needsAttention = channels?.needs_attention?.includes(channel.channel);

          return (
            <div
              key={channel.channel}
              className={`bg-white border rounded-xl p-4 ${
                isTopPerformer
                  ? 'border-primary-300 ring-2 ring-primary-100'
                  : needsAttention
                  ? 'border-amber-300'
                  : 'border-slate-200'
              }`}
            >
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-slate-700">
                  {CHANNEL_NAMES[channel.channel]}
                </span>
                {isTopPerformer && (
                  <span className="text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded-full">
                    Top
                  </span>
                )}
                {needsAttention && (
                  <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                    Watch
                  </span>
                )}
              </div>
              <p className="font-mono text-xl font-bold text-slate-900">
                {formatCurrency(channel.revenue, { compact: true })}
              </p>
              <div className="flex items-center gap-1 mt-2">
                <span
                  className="text-xs font-mono px-1.5 py-0.5 rounded"
                  style={{ backgroundColor: `${CHART_COLORS[index]}20`, color: CHART_COLORS[index] }}
                >
                  {channel.roas}x ROAS
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue & Spend Chart */}
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <h3 className="font-heading font-semibold text-lg text-slate-900 mb-4">
            Revenue vs Spend by Channel (₹L)
          </h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={revenueData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#64748B' }} />
              <YAxis tick={{ fontSize: 12, fill: '#64748B' }} tickFormatter={(v) => `₹${v}L`} />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #E2E8F0',
                  borderRadius: '8px',
                }}
                formatter={(value) => [`₹${value.toFixed(1)}L`, '']}
              />
              <Bar dataKey="revenue" fill={CHART_COLORS[0]} name="Revenue" radius={[4, 4, 0, 0]} />
              <Bar dataKey="spend" fill={CHART_COLORS[2]} name="Spend" radius={[4, 4, 0, 0]} />
              <Bar dataKey="margin" fill={CHART_COLORS[1]} name="Contribution" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* ROAS Radar Chart */}
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <h3 className="font-heading font-semibold text-lg text-slate-900 mb-4">
            Channel ROAS Comparison
          </h3>
          <ResponsiveContainer width="100%" height={280}>
            <RadarChart data={roasData}>
              <PolarGrid stroke="#E2E8F0" />
              <PolarAngleAxis dataKey="subject" tick={{ fontSize: 11, fill: '#64748B' }} />
              <PolarRadiusAxis angle={30} domain={[0, 6]} tick={{ fontSize: 10, fill: '#64748B' }} />
              <Radar
                name="ROAS"
                dataKey="ROAS"
                stroke={CHART_COLORS[0]}
                fill={CHART_COLORS[0]}
                fillOpacity={0.3}
              />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Detailed Metrics Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="p-6 border-b border-slate-100">
          <h3 className="font-heading font-semibold text-lg text-slate-900">Detailed Channel Metrics</h3>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Channel</TableHead>
                <TableHead className="text-right">Revenue</TableHead>
                <TableHead className="text-right">Spend</TableHead>
                <TableHead className="text-right">ROAS</TableHead>
                <TableHead className="text-right">CAC</TableHead>
                <TableHead className="text-right">LTV</TableHead>
                <TableHead className="text-right">Conv %</TableHead>
                <TableHead className="text-right">AOV</TableHead>
                <TableHead className="text-right">Repeat %</TableHead>
                <TableHead className="text-right">Return %</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {channels?.channels?.map((channel, index) => (
                <TableRow key={channel.channel} className="hover:bg-slate-50">
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: CHART_COLORS[index] }}
                      />
                      <span className="font-medium">{CHANNEL_NAMES[channel.channel]}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {formatCurrency(channel.revenue, { compact: true })}
                  </TableCell>
                  <TableCell className="text-right font-mono text-slate-500">
                    {formatCurrency(channel.spend, { compact: true })}
                  </TableCell>
                  <TableCell className="text-right">
                    <span className={`font-mono font-semibold ${channel.roas >= 3 ? 'text-emerald-600' : 'text-amber-600'}`}>
                      {channel.roas}x
                    </span>
                  </TableCell>
                  <TableCell className="text-right font-mono">₹{channel.cac}</TableCell>
                  <TableCell className="text-right font-mono">₹{channel.ltv}</TableCell>
                  <TableCell className="text-right font-mono">{channel.conversion_rate}%</TableCell>
                  <TableCell className="text-right font-mono">₹{channel.aov}</TableCell>
                  <TableCell className="text-right">
                    <span className={`font-mono ${channel.repeat_rate >= 30 ? 'text-emerald-600' : 'text-slate-600'}`}>
                      {channel.repeat_rate}%
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <span className={`font-mono ${channel.return_rate <= 5 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {channel.return_rate}%
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Unit Economics Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <DollarSign className="w-5 h-5 text-primary-600" />
            <span className="text-sm text-slate-500">Blended ROAS</span>
          </div>
          <p className="font-mono text-2xl font-bold text-slate-900">
            {(channels?.channels?.reduce((acc, c) => acc + c.roas, 0) / channels?.channels?.length).toFixed(1)}x
          </p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-5 h-5 text-emerald-600" />
            <span className="text-sm text-slate-500">Avg CAC</span>
          </div>
          <p className="font-mono text-2xl font-bold text-slate-900">
            ₹{Math.round(channels?.channels?.reduce((acc, c) => acc + c.cac, 0) / channels?.channels?.length)}
          </p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <ShoppingCart className="w-5 h-5 text-amber-600" />
            <span className="text-sm text-slate-500">Avg AOV</span>
          </div>
          <p className="font-mono text-2xl font-bold text-slate-900">
            ₹{Math.round(channels?.channels?.reduce((acc, c) => acc + c.aov, 0) / channels?.channels?.length)}
          </p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <RefreshCw className="w-5 h-5 text-blue-600" />
            <span className="text-sm text-slate-500">Avg Repeat Rate</span>
          </div>
          <p className="font-mono text-2xl font-bold text-slate-900">
            {Math.round(channels?.channels?.reduce((acc, c) => acc + c.repeat_rate, 0) / channels?.channels?.length)}%
          </p>
        </div>
      </div>
    </div>
  );
}
